import type { AxiosError } from "axios";

/** What a screen needs from a failed call: core's code and a sentence to show. */
export class BusinessApiError extends Error {
  code: string;
  status: number | null;
  /** Whether the answer carried one of core's own codes, so we know core said it. */
  fromCore: boolean;
  constructor(
    code: string,
    message: string,
    status: number | null,
    fromCore = true,
  ) {
    super(message);
    this.name = "BusinessApiError";
    this.code = code;
    this.status = status;
    this.fromCore = fromCore;
  }
}

/** Ops wording for the codes core documents. Anything else gets the fallback. */
const WORDS: Record<string, string> = {
  pan_not_valid: "PAN not valid. Check it and try again.",
  not_a_company_pan: "This PAN is not a company PAN. Enter the business PAN.",
  already_registered: "This PAN is already registered, or has an application.",
  pan_check_limit: "PAN check limit reached. Try again later.",
  application_exists: "This PAN is already registered, or has an application.",
  email_in_use: "This email is already in use. Use a different email.",
  items_open: "Some items still need a decision.",
  not_in_review: "This application is not waiting for a decision.",
  already_decided: "This item has already been decided.",
  no_open_invite: "There is no open invite to resend.",
  item_held: "This item was sent back. The next try is a new item.",
  legal_name_required:
    "The PAN check did not return a name. Enter the company name.",
  not_onboarding: "This company is not waiting for approval.",
  unknown_product: "This product is no longer offered. Refresh and try again.",
  product_not_in_catalogue:
    "This product is no longer offered. Refresh and try again.",
  note_required: "Add a note for the business.",
  unknown_reason: "Pick one of the listed reasons.",
  reason_required: "A reason is required.",
  business_not_found: "We couldn't find this business.",
  review_item_not_found: "We couldn't find this item.",
  missing_permission: "You do not have permission for this.",
};

export const FALLBACK_WORDS = "Something went wrong. Try again.";

/**
 * True when the call may have reached core, so we cannot say nothing was saved:
 * no response at all (timeout, abort, offline, refused, reset), a 5xx or 408,
 * or an error that is not core's own (a gateway page, an unexpected throw).
 * Only a 4xx that carries one of core's codes is a refusal we can vouch for.
 */
export function outcomeUnknown(error: unknown): boolean {
  if (!(error instanceof BusinessApiError)) return true;
  return (
    error.status === null ||
    error.status >= 500 ||
    error.status === 408 ||
    !error.fromCore
  );
}

export function toBusinessError(error: unknown): BusinessApiError {
  if (error instanceof BusinessApiError) return error;
  const err = error as AxiosError<{ code?: string; message?: string }>;
  const given = err?.response?.data?.code;
  const code = typeof given === "string" && given ? given : "unknown";
  return new BusinessApiError(
    code,
    WORDS[code] ?? FALLBACK_WORDS,
    err?.response?.status ?? null,
    code !== "unknown",
  );
}

/** Build an error from a code alone, with the wording above. Used by the mock. */
export function businessError(code: string, status: number): BusinessApiError {
  return new BusinessApiError(
    code,
    WORDS[code] ?? FALLBACK_WORDS,
    status,
    code !== "unknown",
  );
}
